import { getISODay, setHours, setISODay, setMinutes, startOfDay } from 'date-fns';

/**
 * A recurring schedule slot: ISO weekday plus "HH:mm" start/end times.
 * The backend stores and expands these in UTC, so every form that edits them
 * and every view that prints them has to convert from/to the viewer's zone.
 */
export interface ScheduleSlot {
  day: number;
  startT: string;
  endT: string;
}

const pad = (value: number) => value.toString().padStart(2, '0');

const toIsoDay = (weekday: number) => (weekday === 0 ? 7 : weekday);

// Accepts "HH:mm" as well as "HH:mm:ss" — the schema allows both.
const splitTime = (time: string): [number, number] => {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  return [hours, minutes];
};

/**
 * Converts a slot entered in local time to the UTC slot the backend expects.
 * The weekday moves with the start time, so Monday 00:30 in Warsaw becomes
 * Sunday 22:30/23:30 UTC. `reference` picks which week's offset (DST) applies.
 */
export function scheduleSlotToUtc<T extends ScheduleSlot>(slot: T, reference: Date = new Date()): T {
  const dayBase = startOfDay(setISODay(reference, slot.day));
  const atLocal = (time: string) => {
    const [hours, minutes] = splitTime(time);
    return setMinutes(setHours(dayBase, hours), minutes);
  };
  const start = atLocal(slot.startT);
  const end = atLocal(slot.endT);

  return {
    ...slot,
    day: toIsoDay(start.getUTCDay()),
    startT: `${pad(start.getUTCHours())}:${pad(start.getUTCMinutes())}`,
    endT: `${pad(end.getUTCHours())}:${pad(end.getUTCMinutes())}`,
  };
}

/**
 * Converts a UTC slot from the backend to the viewer's local time — the
 * inverse of {@link scheduleSlotToUtc}.
 */
export function scheduleSlotToLocal<T extends ScheduleSlot>(slot: T, reference: Date = new Date()): T {
  const dayBase = setISODay(reference, slot.day);
  const atUtc = (time: string) => {
    const [hours, minutes] = splitTime(time);
    return new Date(Date.UTC(dayBase.getFullYear(), dayBase.getMonth(), dayBase.getDate(), hours, minutes));
  };
  const start = atUtc(slot.startT);
  const end = atUtc(slot.endT);

  return {
    ...slot,
    day: getISODay(start),
    startT: `${pad(start.getHours())}:${pad(start.getMinutes())}`,
    endT: `${pad(end.getHours())}:${pad(end.getMinutes())}`,
  };
}
